import { GenericCredentials, AuthProvider, AuthProviderForm, SessionClaims, Session } from '../sessions.service';
import * as jose from 'jose';

/**
 * Interface for OAuth2 grant types.
 */
export interface OAuth2GrantType {
  /**
   * Prepares the authentication payload for the grant type.
   *
   * @param credentials The credentials to use for authentication.
   * @param config The OAuth2 auth provider configuration.
   * @returns A promise that resolves to the prepared authentication payload.
   */
  prepareAuthenticationPayload(credentials: GenericCredentials, config: OAuth2AuthProviderConfig): Promise<BodyInit>;

  /**
   * Gets the sign-in fields for the grant type.
   *
   * @returns The sign-in fields.
   */
  getSignInFields(): AuthProviderForm;
}

/**
 * Interface for OAuth2 auth provider configuration.
 */
export interface OAuth2AuthProviderConfig {
  /**
   * The grant type to use for authentication.
   */
  grantType: OAuth2GrantType;

  /**
   * The redirect URL for the auth provider.
   */
  redirectUrl: string;

  /**
   * The authorization URL for the auth provider (optional).
   */
  authorizationUrl?: string;

  /**
   * The token URL for the auth provider.
   */
  tokenUrl: string;

  /**
   * The client ID for the auth provider.
   */
  clientId: string;

  /**
   * The client secret for the auth provider (optional).
   */
  clientSecret?: string;

  /**
   * The scopes to request for the auth provider.
   */
  scopes: string[];

  /**
   * The label for the auth provider (optional).
   */
  label?: string;

  /**
   * The icon for the auth provider (optional).
   */
  icon?: string;

  /**
   * The timeout in seconds for authorization codes (optional).
   */
  authorizationCodeTimeoutSeconds?: number;

  /**
   * Extracts claims from a token.
   *
   * @param access_token The access token.
   * @param id_token The ID token (optional).
   * @returns A promise that resolves to the extracted claims.
   */
  extractClaimsFromToken: (access_token: jose.JWTPayload, id_token?: jose.JWTPayload) => Promise<SessionClaims>;

  /**
   * Extracts a session ID from a token.
   *
   * @param access_token The access token.
   * @param id_token The ID token (optional).
   * @returns A promise that resolves to the extracted session ID.
   */
  extractSessionIdFromToken: (access_token: jose.JWTPayload, id_token?: jose.JWTPayload) => Promise<string>;
}

/**
 * Class representing an OAuth2 auth provider.
 */
export default class OAuth2AuthProvider implements AuthProvider {
  /**
   * The label for the auth provider.
   */
  public label: string;

  /**
   * The icon for the auth provider.
   */
  public icon: string;

  /**
   * Constructs a new OAuth2 auth provider.
   *
   * @param name The name of the auth provider.
   * @param config The configuration for the auth provider.
   */
  public constructor(
    public name: string,
    private config: OAuth2AuthProviderConfig
  ) {
    this.label = config.label ?? name;
    this.icon = config.icon ?? 'fa-key';
  }

  /**
   * Starts a new session with the auth provider.
   *
   * @param credentials The credentials to use for authentication.
   * @returns A promise that resolves to the new session.
   */
  async startSession(credentials: GenericCredentials): Promise<Session> {
    let formData: BodyInit = await this.config.grantType.prepareAuthenticationPayload(credentials, this.config);

    const result = await fetch(this.config.tokenUrl, {
      method: 'POST',
      body: formData,
    });
    const responseBody = await result.json();

    console.debug({
      message: 'Response from IDP',
      response: responseBody,
    });

    return this.makeSessionFromIdpResponse(credentials, responseBody);
  }

  /**
   * Creates a new session from an IDP response.
   *
   * @param credentials The credentials used for authentication.
   * @param responseBody The response body from the IDP.
   * @returns A promise that resolves to the new session.
   */
  private async makeSessionFromIdpResponse(credentials: GenericCredentials, responseBody: any): Promise<Session> {
    const expires_in = typeof responseBody['expires_in'] === 'number' ? responseBody['expires_in'] : 0;

    const access_token = responseBody['access_token'];
    const access_token_payload = jose.decodeJwt(access_token) as jose.JWTPayload;

    const refreshToken = responseBody['refresh_token'];
    const refreshCredentials: GenericCredentials = refreshToken ? { refreshToken: refreshToken } : {};

    const id_token = responseBody['id_token'];
    const id_token_payload = id_token ? (jose.decodeJwt(id_token) as jose.JWTPayload) : {};

    const sessionId = await this.config.extractSessionIdFromToken(access_token_payload, id_token_payload);
    const sessionClaims: SessionClaims = await this.config.extractClaimsFromToken(
      access_token_payload,
      id_token_payload
    );

    const prefillInfo = Object.fromEntries(
      this.getSignInFields()
        .filter((field) => field.key in credentials && field.saveForPrefill)
        .map((field) => [field.key, credentials[field.key]])
    );

    const session = new Session(
      sessionId,
      this.name,
      credentials,
      refreshCredentials,
      prefillInfo,
      {
        httpHeaders: {
          Authorization: `Bearer ${access_token}`,
        },
      },
      new Date().getTime(),
      sessionClaims,
      new Date().getTime() + (expires_in ?? 0) * 1000,
      null,
      null
    );

    return session;
  }

  /**
   * Refreshes an existing session with the auth provider.
   *
   * @param sessionToRefresh The session to refresh.
   * @returns A promise that resolves to the refreshed session.
   */
  async refreshSession(sessionToRefresh: Session): Promise<Session> {
    if (!sessionToRefresh.refreshCredentials) {
      throw new Error('No refresh credentials defined');
    }

    const refreshToken = sessionToRefresh.refreshCredentials['refreshToken'];
    const requestBody = this.makeRefreshRequestBody(refreshToken);

    const result = await fetch(this.config.tokenUrl, {
      method: 'POST',
      body: requestBody,
    });

    if (!result.ok) {
      console.debug('Error [OAuth2]: ', result.text());
      throw new Error('Failed to refresh session');
    }

    const responseBody = await result.json();

    const refreshedSession = await this.makeSessionFromIdpResponse(
      sessionToRefresh.authCredentials ?? {},
      responseBody
    );
    refreshedSession.prefillInfo = sessionToRefresh.prefillInfo;

    return refreshedSession;
  }

  /**
   * Creates a refresh request body.
   *
   * @param refreshToken The refresh token to use.
   * @returns The refresh request body.
   */
  private makeRefreshRequestBody(refreshToken: string): BodyInit {
    const formData = new URLSearchParams();
    formData.append('grant_type', 'refresh_token');
    formData.append('refresh_token', refreshToken);
    formData.append('client_id', this.config.clientId);
    if (this.config.clientSecret) {
      formData.append('client_secret', this.config.clientSecret);
    }
    return formData;
  }

  /**
   * Gets the sign-in fields for the auth provider.
   *
   * @returns The sign-in fields.
   */
  public getSignInFields(): AuthProviderForm {
    return this.config.grantType.getSignInFields();
  }
}
